import { type TypedDocumentNode, gql } from '@apollo/client';
import { FormattedMessage } from 'react-intl';
import styled from 'styled-components';

import { Horizontal, Vertical } from '@sorare/core/src/atoms/layout/flex';
import { BrandXS, LabelS } from '@sorare/core/src/atoms/typography';
import { CardImg } from '@sorare/core/src/components/card/CardImg';
import { OpenItemDialogLink } from '@sorare/core/src/components/link/OpenItemDialogLink';
import { getPositionInitials } from '@sorare/core/src/lib/positions';

import { CardDescription } from '#src/components/token/CardDescription/index.tsx';

import type { InlineCard_anyCardInterface } from './__generated__/index.graphql.ts';

const CardWrapper = styled(Vertical)`
  > img {
    width: calc(5 * var(--unit));
    height: calc(5 * var(--unit));
  }
  flex-shrink: 0;
  border-radius: var(--half-unit);
  overflow: hidden;
`;

type Props = {
  anyCard: InlineCard_anyCardInterface;
  withPositions?: boolean;
};

export const InlineCard = ({ anyCard, withPositions = false }: Props) => {
  return (
    <OpenItemDialogLink item={anyCard} sport={anyCard.sport}>
      <Horizontal>
        <CardWrapper>
          <CardImg src={anyCard.squaredPictureUrl} width={80} />
        </CardWrapper>
        <Vertical gap={0}>
          <CardDescription
            Title={BrandXS}
            Details={LabelS}
            detailsColor="var(--c-nd-600)"
            card={anyCard}
            withoutLink
          />
          {!!withPositions && (
            <Horizontal gap={0.5}>
              <LabelS color="var(--c-nd-600)">
                {anyCard.anyPlayer.anyPositions
                  .map(position => getPositionInitials(position))
                  .join('/')}
                {' - '}
                <FormattedMessage
                  defaultMessage="{age} yo"
                  id="RecentPurchase.age"
                  values={{ age: anyCard.anyPlayer.age }}
                />
              </LabelS>
            </Horizontal>
          )}
        </Vertical>
      </Horizontal>
    </OpenItemDialogLink>
  );
};

InlineCard.fragments = {
  anyCard: gql`
    fragment InlineCard_anyCardInterface on AnyCardInterface {
      slug
      sport
      squaredPictureUrl: pictureUrl(derivative: "avatar")
      anyPlayer {
        slug
        anyPositions
        age
      }
      ...CardDescription_anyCard
    }
    ${CardDescription.fragments.anyCard}
  ` as TypedDocumentNode<InlineCard_anyCardInterface>,
};
